import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScrollText, Download, X, Loader2, MessageSquare, Send } from 'lucide-react';
import '../styles/PatientReportButton.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/**
 * Patient-side affordance that appears on each case card. Polls (lazily) for a
 * released clinical report on mount; when one exists, the button activates and
 * lets the patient view inline + download the PDF.
 */
const ClinicalReportButton = ({ caseItem, token }) => {
  const [available, setAvailable] = useState(false);
  const [released, setReleased] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showThread, setShowThread] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!caseItem?.id || !token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${API_URL}/api/portal/cases/${caseItem.id}/clinical-report`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        setAvailable(false);
        return;
      }
      const data = await res.json();
      setAvailable(!!data.available);
      setReleased(data.released || null);
    } catch {
      setAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [caseItem?.id, token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await fetch(
        `${API_URL}/api/portal/cases/${caseItem.id}/clinical-report/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CADENCE_Report_${caseItem.case_number || 'case'}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !available) return null;

  return (
    <>
      <button
        className="btn-outline patient-report-btn"
        onClick={() => setOpenModal(true)}
        data-testid={`patient-report-btn-${caseItem.id}`}
      >
        <ScrollText size={16} />
        View My Report
      </button>

      {openModal && released && (
        <div
          className="patient-report-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenModal(false);
          }}
        >
          <div className="patient-report-modal-card">
            <header className="patient-report-modal-head">
              <div>
                <h2>Your CADENCE Clinical Report</h2>
                <p>Case {caseItem.case_number}</p>
              </div>
              <button
                className="patient-report-close"
                onClick={() => setOpenModal(false)}
                aria-label="Close"
                data-testid="patient-report-close-btn"
              >
                <X size={22} />
              </button>
            </header>

            <div className="patient-report-body">
              {(released.last_updated_at || released.released_at) && (
                <div className="patient-report-stamp" data-testid="patient-report-stamp">
                  <span>
                    <strong>Last updated:</strong>{' '}
                    {formatDate(released.last_updated_at || released.released_at)}
                  </span>
                  {released.version && (
                    <span className="patient-report-version">Version {released.version}</span>
                  )}
                </div>
              )}
              <ReleasedNarrative title="Summary of Key Patterns" items={released.summary} />
              <ReleasedGaps title="Identified Gaps and Contradictions" items={released.gaps} />
              <ReleasedNextSteps title="Recommended Next Steps" items={released.next_steps} />

              <div className="patient-report-thread-cta">
                <button
                  type="button"
                  className="patient-report-thread-btn"
                  onClick={() => setShowThread((v) => !v)}
                  data-testid="patient-report-thread-toggle-btn"
                >
                  <MessageSquare size={16} />
                  {showThread ? 'Hide thread' : 'I have a question about this report'}
                </button>
                {showThread && (
                  <ReportQuestionThread caseItem={caseItem} token={token} />
                )}
              </div>

              <p className="patient-report-disclaimer">
                CADENCE Medical Consulting, LLC provides informational and advocacy
                services. This report does not replace your healthcare team. Bring it
                with you to your next appointment.
              </p>
            </div>

            <footer className="patient-report-modal-foot">
              <button
                className="patient-report-download"
                onClick={handleDownload}
                disabled={downloading}
                data-testid="patient-report-download-btn"
              >
                {downloading ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                Download PDF
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

const ReleasedNarrative = ({ title, items }) => {
  const included = (items || []).filter((p) => p.included);
  if (!included.length) return null;
  return (
    <section className="patient-report-section">
      <h3>{title}</h3>
      {included.map((p) => (
        <div key={p.id} className="patient-report-paragraph">
          {p.heading && <h4>{p.heading}</h4>}
          <p>{p.body}</p>
        </div>
      ))}
    </section>
  );
};

const ReleasedGaps = ({ title, items }) => {
  const included = (items || []).filter((g) => g.included);
  if (!included.length) return null;
  return (
    <section className="patient-report-section">
      <h3>{title}</h3>
      {included.map((g) => (
        <div key={g.id} className="patient-report-paragraph">
          {g.heading && <h4>{g.heading}</h4>}
          {g.body && <p>{g.body}</p>}
          {(g.items || []).length > 0 && (
            <ol className="patient-report-gaps-list">
              {g.items.map((it) => (
                <li key={it.id || it.lead}>
                  <strong>{it.lead}</strong>
                  {it.explanation && <span> — {it.explanation}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </section>
  );
};

const ReleasedNextSteps = ({ title, items }) => {
  const included = (items || []).filter((p) => p.included);
  if (!included.length) return null;
  const labelMap = {
    critical: 'Critical',
    important: 'Important',
    'if-time-allows': 'If time allows',
  };
  return (
    <section className="patient-report-section">
      <h3>{title}</h3>
      <ul className="patient-report-nextsteps">
        {included.map((it) => (
          <li key={it.id} className={`patient-next-${it.priority || 'important'}`}>
            <span className={`patient-priority patient-priority-${it.priority || 'important'}`}>
              {labelMap[it.priority] || 'Important'}
            </span>
            <span className="patient-next-ask">{it.ask}</span>
            {it.why && <span className="patient-next-why">{it.why}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
};

// ---------------------------------------------------------------------------
// "I have a question about this report" thread — auto-routes to admin
// ---------------------------------------------------------------------------
const ReportQuestionThread = ({ caseItem, token }) => {
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(true);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);

  const loadThread = useCallback(async () => {
    if (!caseItem?.id || !token) return;
    setLoadingThread(true);
    try {
      const res = await fetch(
        `${API_URL}/api/portal/cases/${caseItem.id}/clinical-report/thread`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setMessages(data.messages || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingThread(false);
    }
  }, [caseItem?.id, token]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const res = await fetch(
        `${API_URL}/api/portal/cases/${caseItem.id}/clinical-report/thread`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ content: trimmed }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Send failed');
      setMessages((prev) => [...prev, data.message]);
      setContent('');
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="patient-report-thread" data-testid="patient-report-thread">
      <p className="patient-report-thread-help">
        Your message goes directly to the CADENCE Care Team for this case. Replies
        will appear here and via your portal notifications.
      </p>
      <div className="patient-report-thread-list" ref={listRef}>
        {loadingThread && <p className="patient-report-thread-empty">Loading…</p>}
        {!loadingThread && messages.length === 0 && (
          <p className="patient-report-thread-empty">
            No messages yet. Type your question below to start the thread.
          </p>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`patient-report-thread-bubble bubble-${m.author_role}`}
            data-testid={`patient-report-thread-msg-${m.id}`}
          >
            <div className="bubble-meta">
              <strong>{m.author_name}</strong>
              <span>{formatDate(m.created_at)}</span>
            </div>
            <p>{m.content}</p>
          </div>
        ))}
      </div>
      {error && <p className="patient-report-thread-error">{error}</p>}
      <form onSubmit={handleSend} className="patient-report-thread-form">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What part of your report would you like to revisit?"
          rows={3}
          maxLength={4000}
          data-testid="patient-report-thread-input"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !content.trim()}
          data-testid="patient-report-thread-send-btn"
        >
          {sending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
          Send
        </button>
      </form>
    </div>
  );
};

export default ClinicalReportButton;
